import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Text,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Switch,
} from '@chakra-ui/react';
import { 
  FiSearch, 
  FiMoreVertical, 
  FiEdit, 
  FiTrash2, 
  FiUserPlus, 
  FiKey,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import userService from '../../services/api/userService';
import config from '../../config/config';
import { useForm } from 'react-hook-form';

/**
 * UserManagement component
 * Admin page for managing users (admins and committee members)
 */
const UserManagement = () => {
  const toast = useToast();
  const { 
    isOpen: isUserModalOpen, 
    onOpen: onUserModalOpen, 
    onClose: onUserModalClose 
  } = useDisclosure();
  const { 
    isOpen: isDeleteModalOpen, 
    onOpen: onDeleteModalOpen, 
    onClose: onDeleteModalClose 
  } = useDisclosure();
  const { 
    isOpen: isResetPasswordModalOpen, 
    onOpen: onResetPasswordModalOpen, 
    onClose: onResetPasswordModalClose 
  } = useDisclosure();
  
  // State
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  
  // React Hook Form
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();
  
  // Pagination
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Fetch users data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const result = await userService.getAllUsers();
        
        if (result.error) {
          throw new Error(result.message || 'Failed to fetch users');
        }
        
        // Ensure we have an array of users
        const usersList = Array.isArray(result) ? result : [];
        
        // Map backend branch_code to frontend branchCode for consistency
        const processedUsers = usersList.map(user => ({
          ...user,
          branchCode: user.branch_code || user.branchCode || user.branchId,
          centerCode: user.center_code || user.centerCode || user.centerId
        }));
        
        setUsers(processedUsers);
        setFilteredUsers(processedUsers);
      } catch (error) {
        toast({
          title: 'خطأ في جلب البيانات',
          description: error.message || 'حدث خطأ أثناء جلب بيانات المستخدمين',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [toast]);

  // Apply filters
  useEffect(() => {
    let filtered = [...users];
    
    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }
    
    // Apply branch filter
    if (branchFilter !== 'all') {
      filtered = filtered.filter(
        (user) => (user.branchCode === branchFilter || user.branch_code === branchFilter || user.branchId === branchFilter)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(
        (user) => user.is_active === (statusFilter === 'active')
      );
    }
    
    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, roleFilter, branchFilter, statusFilter, users]);

  // Handle add/edit user
  const handleUserModalOpen = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setIsEditing(true);
      
      // Set form values
      setValue('full_name', user.name || user.full_name);
      setValue('email', user.email);
      setValue('role', user.role);
      setValue('branchCode', user.branchCode || user.branch_code || user.branchId); // Support all field name variations
      setValue('centerCode', user.centerCode || user.center_code || user.centerId); // Support all field name variations
      setValue('is_active', user.is_active);
    } else {
      setSelectedUser(null);
      setIsEditing(false);
      reset();
    }
    
    onUserModalOpen();
  };

  // Handle user form submit
  const onUserFormSubmit = async (data) => {
    try {
      // Ensure proper field names for backend compatibility
      const formData = {
        ...data,
      };
      
      // Only add branch_code and center_code if they have actual values
      if (data.branchCode) {
        formData.branch_code = data.branchCode;
      }
      
      if (data.centerCode) {
        formData.center_code = data.centerCode;
      }

      // Remove frontend-specific field names to avoid duplication
      delete formData.branchCode;
      delete formData.centerCode;
      delete formData.branchId;

      if (isEditing) {
        // Update existing user
        const result = await userService.updateUser(selectedUser.id, formData);
        
        if (result.error) {
          throw new Error(result.message || 'Failed to update user');
        }
        
        // Update local state
        setUsers(users.map((user) => (user.id === selectedUser.id ? { ...user, ...result } : user)));
        
        toast({
          title: 'تم التحديث بنجاح',
          description: 'تم تحديث بيانات المستخدم بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        // Create new user
        const result = await userService.createUser(formData);
        
        if (result.error) {
          throw new Error(result.message || 'Failed to create user');
        }
        
        // Update local state with the newly created user
        setUsers(prevUsers => [...prevUsers, result]);
        
        toast({
          title: 'تم الإضافة بنجاح',
          description: 'تم إضافة المستخدم الجديد بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
      
      onUserModalClose();
    } catch (error) {
      toast({
        title: 'خطأ في العملية',
        description: error.message || 'حدث خطأ أثناء معالجة طلبك',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Handle delete user
  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    onDeleteModalOpen();
  };

  // Confirm delete user
  const confirmDeleteUser = async () => {
    try {
      const result = await userService.deleteUser(selectedUser.id);
      
      if (result.error) {
        throw new Error(result.message || 'Failed to delete user');
      }
      
      // Update local state by removing the deleted user
      setUsers(prevUsers => prevUsers.filter((user) => user.id !== selectedUser.id));
      
      toast({
        title: 'تم الحذف بنجاح',
        description: 'تم حذف المستخدم بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      
      onDeleteModalClose();
    } catch (error) {
      toast({
        title: 'خطأ في الحذف',
        description: error.message || 'حدث خطأ أثناء حذف المستخدم',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Handle reset password
  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    onResetPasswordModalOpen();
  };

  // Confirm reset password
  const confirmResetPassword = async () => {
    try {
      if (!newPassword || newPassword.length < 6) {
        throw new Error('يجب أن تكون كلمة المرور الجديدة 6 أحرف على الأقل');
      }
      
      const result = await userService.resetUserPassword(selectedUser.id, newPassword);
      
      if (result.error) {
        throw new Error(result.message || 'Failed to reset password');
      }
      
      toast({
        title: 'تم إعادة تعيين كلمة المرور',
        description: 'تم إعادة تعيين كلمة المرور بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      
      // Reset the password field and close the modal
      setNewPassword('');
      onResetPasswordModalClose();
    } catch (error) {
      toast({
        title: 'خطأ في إعادة تعيين كلمة المرور',
        description: error.message || 'حدث خطأ أثناء إعادة تعيين كلمة المرور',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Get branch name
  const getBranchName = (branchCode) => {
    if (!branchCode) return 'المقر الرئيسي';
    // Find branch by ID (branch ID in frontend = branch code in backend)
    const branch = config.organization.branches.find((b) => b.id === branchCode);
    return branch ? branch.name : branchCode; // Return the code itself as fallback
  };
  


  // Get role name in Arabic
  const getRoleName = (role) => {
    switch (role) {
      case 'admin':
        return 'مدير النظام';
      case 'committee':
        return 'عضو لجنة';
      default:
        return role;
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8}>
        <Heading as="h1" size="lg" mb={2}>
          إدارة المستخدمين
        </Heading>
        <Text color="gray.600">
          إدارة مدراء النظام وأعضاء اللجان
        </Text>
      </Box>

      {/* Filters */}
      <Box mb={6} p={4} bg="white" borderRadius="lg" boxShadow="sm">
        <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
          {/* Search */}
          <InputGroup maxW={{ base: '100%', md: '300px' }}>
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="بحث بالاسم أو البريد الإلكتروني"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          {/* Role Filter */}
          <Select
            placeholder="تصفية حسب الدور"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
          >
            <option value="all">جميع الأدوار</option>
            <option value="admin">مدير النظام</option>
            <option value="committee">عضو لجنة</option>
          </Select>

          {/* Branch Filter */}
          <Select
            placeholder="تصفية حسب الفرع"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
          >
            <option value="all">جميع الفروع</option>
            <option value="">المقر الرئيسي</option>
            {config.organization.branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>

          {/* Status Filter */}
          <Select
            placeholder="تصفية حسب الحالة"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
          >
            <option value="all">جميع الحالات</option>
            <option value="active">نشط</option>
            <option value="inactive">غير نشط</option>
          </Select>

          {/* Add User Button */}
          <Button
            leftIcon={<FiUserPlus />}
            colorScheme="primary"
            onClick={() => handleUserModalOpen()}
            ml="auto"
          >
            إضافة مستخدم
          </Button>
        </Stack>
      </Box>

      {/* Users Table */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>الاسم</Th>
              <Th>البريد الإلكتروني</Th>
              <Th>الدور</Th>
              <Th>الفرع</Th>
              <Th>الحالة</Th>
              <Th>الإجراءات</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={10}>
                  جاري تحميل البيانات...
                </Td>
              </Tr>
            ) : currentUsers.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={10}>
                  لا توجد بيانات متاحة
                </Td>
              </Tr>
            ) : (
              currentUsers.map((user) => (
                <Tr key={user.id}>
                  <Td>{user.full_name || user.name}</Td>
                  <Td>{user.email}</Td>
                  <Td>
                    <Badge
                      colorScheme={user.role === 'admin' ? 'purple' : 'blue'}
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {getRoleName(user.role)}
                    </Badge>
                  </Td>
                  <Td>{getBranchName(user.branchCode || user.branchId)}</Td>
                  <Td>
                    <Badge
                      colorScheme={user.is_active ? 'green' : 'red'}
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {user.is_active ? 'نشط' : 'غير نشط'}
                    </Badge>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                        aria-label="المزيد من الخيارات"
                      />
                      <MenuList>
                        <MenuItem icon={<FiEdit />} onClick={() => handleUserModalOpen(user)}>
                          تعديل
                        </MenuItem>
                        <MenuItem icon={<FiKey />} onClick={() => handleResetPassword(user)}>
                          إعادة تعيين كلمة المرور
                        </MenuItem>
                        <MenuItem icon={<FiTrash2 />} onClick={() => handleDeleteUser(user)} color="red.500">
                          حذف
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>

        {/* Pagination */}
        {!loading && filteredUsers.length > 0 && (
          <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
            <Text color="gray.600">
              عرض {startIndex + 1} إلى {Math.min(endIndex, filteredUsers.length)} من أصل {filteredUsers.length} مستخدم
            </Text>
            <Stack direction="row" spacing={2}>
              <IconButton
                icon={<FiChevronRight />}
                aria-label="الصفحة السابقة"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              <IconButton
                icon={<FiChevronLeft />}
                aria-label="الصفحة التالية"
                isDisabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </Stack>
          </Flex>
        )}
      </Box>

      {/* Add/Edit User Modal */}
      <Modal isOpen={isUserModalOpen} onClose={onUserModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? 'تعديل بيانات المستخدم' : 'إضافة مستخدم جديد'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit(onUserFormSubmit)}>
            <ModalBody>
              <Stack spacing={4}>
                <FormControl isInvalid={errors.full_name} isRequired>
                  <FormLabel>الاسم</FormLabel>
                  <Input
                    placeholder="أدخل الاسم الكامل"
                    {...register('full_name', {
                      required: 'الاسم مطلوب',
                    })}
                  />
                  <FormErrorMessage>{errors.full_name?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.email} isRequired>
                  <FormLabel>البريد الإلكتروني</FormLabel>
                  <Input
                    type="email"
                    placeholder="أدخل البريد الإلكتروني"
                    {...register('email', {
                      required: 'البريد الإلكتروني مطلوب',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'البريد الإلكتروني غير صالح',
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>

                {!isEditing && (
                  <FormControl isInvalid={errors.password} isRequired>
                    <FormLabel>كلمة المرور</FormLabel>
                    <Input
                      type="password"
                      placeholder="أدخل كلمة المرور"
                      {...register('password', {
                        required: 'كلمة المرور مطلوبة',
                        minLength: {
                          value: 8,
                          message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل',
                        },
                      })}
                    />
                    <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                  </FormControl>
                )}

                <FormControl isInvalid={errors.role} isRequired>
                  <FormLabel>الدور</FormLabel>
                  <Select
                    placeholder="اختر الدور"
                    {...register('role', {
                      required: 'الدور مطلوب',
                    })}
                  >
                    <option value="admin">مدير النظام</option>
                    <option value="committee">عضو لجنة</option>
                  </Select>
                  <FormErrorMessage>{errors.role?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.branchCode}>
                  <FormLabel>الفرع</FormLabel>
                  <Select
                    placeholder="اختر الفرع"
                    {...register('branchCode')}
                  >
                    <option value="">المقر الرئيسي</option>
                    {config.organization.branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.branchCode?.message}</FormErrorMessage>
                </FormControl>


                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="active" mb="0">
                    حالة المستخدم
                  </FormLabel>
                  <Switch
                    id="active"
                    colorScheme="green"
                    {...register('is_active')}
                    defaultChecked={isEditing ? selectedUser?.is_active : true}
                  />
                </FormControl>
              </Stack>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onUserModalClose}>
                إلغاء
              </Button>
              <Button colorScheme="primary" type="submit">
                {isEditing ? 'تحديث' : 'إضافة'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete User Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>تأكيد الحذف</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            هل أنت متأكد من رغبتك في حذف المستخدم{' '}
            {selectedUser && selectedUser.name}؟
            <Text color="red.500" mt={2}>
              هذا الإجراء لا يمكن التراجع عنه.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onDeleteModalClose}>
              إلغاء
            </Button>
            <Button colorScheme="red" onClick={confirmDeleteUser}>
              تأكيد الحذف
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={isResetPasswordModalOpen} onClose={onResetPasswordModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>إعادة تعيين كلمة المرور</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              أنت على وشك إعادة تعيين كلمة المرور للمستخدم{' '}
              {selectedUser && (selectedUser.full_name || selectedUser.name)}.
            </Text>
            <FormControl isRequired>
              <FormLabel>كلمة المرور الجديدة</FormLabel>
              <Input
                type="password"
                placeholder="أدخل كلمة المرور الجديدة"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onResetPasswordModalClose}>
              إلغاء
            </Button>
            <Button
              colorScheme="blue"
              onClick={confirmResetPassword}
              isDisabled={!newPassword || newPassword.length < 8}
            >
              إعادة تعيين
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default UserManagement;
